import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: forbidden. This is a projection, not a lit object: the
// whole frame is lamp light coming through glass, and a stage wash on top would
// be light arriving from a second place that does not exist.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'liquidlight',
  family: 'organic',
  danceability: 0.62,
  energyLevel: 0.55,
  bpmMin: 70,
  bpmMax: 150,
  movement: 0.45,
  tonality: 0.80,
  prefers: { dynamics: [0.25, 0.90] },
  enabled: true
};

// LiquidLight: coloured oil squashed between two clock glasses, heated by the
// projector lamp, pressed and tilted in time with the music.
//
// WHY THIS IS NOT LAVA. Lava uses metaballs: round blobs with soft edges that
// MERGE. Every one of those three properties is wrong here.
//
//   - the edge is SHARP, held by surface tension
//   - the cells are CRUSHED between two plates, so they are irregular patties,
//     not spheres
//   - they never merge: oil and water are immiscible. They deform and shove
//     each other instead.
//
// The model is an additively weighted Voronoi - an Apollonius diagram - whose
// sites drift. For a pixel, take the nearest site and the next nearest by
// (distance - radius):
//
//     edge = min(-d1, (d2 - d1) * 0.5)
//
// Inside a cell that is positive, and it reaches zero on the contour. Which
// term gets there first is exactly the physics: -d1 runs out where the oil
// simply ends in water, giving a ROUNDED edge, and (d2-d1) runs out where two
// cells are pressed against each other, giving a STRAIGHT one. Nothing decides
// between the two cases; the minimum does it.

// --- the cells ----------------------------------------------------------------
const CELLS_MIN = 14;            // at the ends of a track, when the tier allows
const CELLS_MAX = 22;            // and at its middle
// PLUS 60%. The cells are meant to be CRUSHED against one another, so oil
// should cover most of the plate and clear water show only in the gaps between
// them. At the old radii they floated apart, almost every contour was a free
// rounded edge in water, and the straight pressed boundaries - the thing that
// separates this from a lava lamp - were the exception instead of the rule.
const RADIUS = [0.230, 0.460];   // in half-frame heights
const SITE_SPREAD_X = 2.55;      // gathered, but still across the plate
const SITE_SPREAD_Y = 1.50;
const SITE_DRIFT = 0.013;        // own wander, on top of the convection

// --- the press ----------------------------------------------------------------
// The operator squeezes the two glasses together, and EVERY cell spreads at
// once. That simultaneity is what separates the gesture from a per-cell pulse.
const PRESS_BEAT = 0.18;         // +18% of radius
const PRESS_ATTACK = 0.060;      // seconds to reach it
const PRESS_RELEASE = 0.400;     // and to come back
const PRESS_BAR = 1.55;          // a downbeat presses this much harder
const PRESS_DROP = 2.60;         // and a drop harder still
const WARP_BASE = 0.042;         // contour irregularity at rest
const WARP_BAR = 0.075;          // and under a downbeat

// --- convection ---------------------------------------------------------------
// The lamp heats from below: up the middle, down the sides, very slowly.
const CONVECT = 0.020;
const HEAT_GAIN = 0.10;          // cells long at the centre run a little fatter

// --- the tilt -----------------------------------------------------------------
const TILT_EVERY = [20.0, 40.0]; // seconds between tilts
const TILT_DUR = 3.0;
const TILT_SPEED = 0.115;

// --- the meniscus -------------------------------------------------------------
// A triple line, in PIXELS, because that is how it is seen. The same reasoning
// as sumi.js's drying edge, and just as decisive here: without it a cell is a
// coloured shape, with it the cell is liquid.
const LINE_DARK = 2.0;           // the contour itself
const LINE_BRIGHT = 3.0;         // light gathered by the meniscus, just outside
const LINE_DENSE = 8.0;          // thicker oil just inside
const CENTRE_DENSITY = 0.85;     // how much denser the middle of a patty is
const OIL_GRAIN = 0.08;          // and how unevenly it is dyed
const BRIGHT_MIN = 0.30;
const BRIGHT_HIGH = 0.45;

// Lamp falloff and the rim of the clock glass, both radial and both in the
// aspect-corrected units where a circle is a circle.
const LAMP_FALL = 1.95;          // reaches the edge level AT the frame edge
const DISC_IN = 1.62;
const DISC_OUT = 2.10;

const SAFE_FLOOR = 0.50;
const PEAK = 0.98;

// SET FROM THE PI, not from the brief's wish list. The per-pixel search is
// O(cells) and it dominates everything: measured at 1280x400, twenty-two cells
// cost 95 ms a frame - eleven fps - and no amount of trimming elsewhere
// recovers that. The numbers each tier can afford are in the report; tier 2 is
// a Mac and TV configuration and the Pi's own governor will not choose it.
const TIER = {
  0: { cells: 10, lines: 1, translucent: false, disc: false, octaves: 1 },
  1: { cells: 14, lines: 2, translucent: false, disc: true, octaves: 1 },
  2: { cells: 22, lines: 3, translucent: true, disc: true, octaves: 2 }
};
const DEFAULT_TIER = 2;

const _col = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };

function clamp01(x) { return x < 0 ? 0 : (x > 1 ? 1 : x); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function hash1(n) {
  const s = Math.sin(n * 127.1) * 43758.5453;
  return s - Math.floor(s);
}

// =============================================================================
// SHADER
// =============================================================================

const QUAD_VERTEX = `
  precision mediump float;
  varying vec2 vUv;
  varying float vSafe;
${SAFE_ZONE_VERTEX_CHUNK}
  void main() {
    vUv = uv;
    vec4 clip = vec4(position.xy * 2.0, 0.0, 1.0);
    vSafe = safeZoneAttenuation(clip);
    gl_Position = clip;
  }
`;

function fragment(layout, prof) {
  const N = layout.cells;

  const translucent = layout.translucent ? `
    // TRANSLUCENT, and the overlap MULTIPLIES. Where two lenses press together
    // the light goes through both, which is a coloured filter and not an
    // additive mix: the result is darker and more saturated than either, never
    // lighter. Additive blending here would read as glowing plastic.
    float share = 1.0 - smoothstep(0.0, uBandPx, (d2 - d1) * 0.5 / max(fwidth(edge), 1e-6));
    oil = mix(oil, oil * c2 * 1.75, share * 0.85);
  ` : '';

  const brightLine = layout.lines >= 2 ? `
    // The bright line, just OUTSIDE the contour: the meniscus is a lens and it
    // gathers the lamp into a thread of light around every cell.
    // It sits OUTSIDE the dark line and does not overlap it: at -4..0 the two
    // bands shared -2..0 and the bright one simply erased the dark one, which
    // left a single white thread where the brief asks for three.
    // SYMMETRIC in |px|, and that is the whole correction. edge is
    // min(-d1, (d2-d1)*0.5): both terms are positive anywhere inside the oil,
    // so px only ever goes NEGATIVE in clear water. A bright line placed at
    // negative px therefore could not appear at a cell-cell boundary at all -
    // measured as flatly absent there - and once the cells are pressed together
    // almost every contour is one. A meniscus between two pressed lenses
    // gathers light on both sides of the contact line anyway, so the band runs
    // either way from the dark line and clears it.
    float bright = smoothstep(${(LINE_BRIGHT + 1.5).toFixed(1)}, 1.5, abs(px))
                 * smoothstep(1.0, 1.5, abs(px));
    col += vec3(1.0, 0.97, 0.90) * bright * (${BRIGHT_MIN.toFixed(2)} + ${BRIGHT_HIGH.toFixed(2)} * uHigh);
  ` : '';

  const disc = layout.disc ? `
    // THE PROJECTION DISC. We are looking through a round clock glass, so the
    // corners of the frame catch its rim. Tiny, and very particular to the
    // medium.
    col *= 1.0 - smoothstep(${DISC_IN.toFixed(2)}, ${DISC_OUT.toFixed(2)}, rad) * 0.42;
  ` : '';

  return `
  precision mediump float;
  uniform vec4 uSite[${N}];        // xy centre, z radius, w seed
  uniform vec3 uColor[${N}];
  uniform float uPress;            // global radius multiplier - the squeeze
  uniform float uWarp;
  uniform float uHigh;
  uniform float uAspect;
  uniform float uBandPx;
  uniform float uTime;
  uniform vec3 uWater;
  uniform vec3 uWaterEdge;
  uniform float uSites;            // ?liqsites=1
  varying vec2 vUv;
  varying float vSafe;

  float hash12(vec2 p) {
    vec3 q = fract(vec3(mod(p.x, 256.0), mod(p.y, 256.0), mod(p.x, 256.0)) * 0.1031);
    q += dot(q, q.yzx + 33.33);
    return fract((q.x + q.y) * q.z);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash12(i), hash12(i + vec2(1.0, 0.0)), f.x),
               mix(hash12(i + vec2(0.0, 1.0)), hash12(i + vec2(1.0, 1.0)), f.x), f.y);
  }

  void main() {
    // Aspect-corrected, so a cell is round rather than stretched.
    vec2 sp = vec2((vUv.x - 0.5) * 2.0 * uAspect, (vUv.y - 0.5) * 2.0);

    // THE IRREGULARITY, computed ONCE for the pixel and applied to the position
    // the distances are measured from - not once per site inside the loop. Two
    // noise lookups a pixel instead of ${N} of them, for the same result: the
    // contours come out ragged, and differently ragged for every cell, because
    // each one sits in a different part of the warp field. Without it the
    // Voronoi is plainly visible and the picture looks computed.
    // Two octaves, and the high one carries the ripple. At a single 2.4 the
    // field was barely four cells across the frame, so it translated whole
    // cells rather than roughening their contours, and the Voronoi stayed
    // plainly visible.
    vec2 w1 = vec2(vnoise(sp * 4.2 + uTime * 0.05),
                   vnoise(sp * 4.2 + vec2(17.3, 5.1) - uTime * 0.04)) - 0.5;
${layout.octaves >= 2 ? `    vec2 w2 = vec2(vnoise(sp * 9.5 - uTime * 0.09),
                   vnoise(sp * 9.5 + vec2(3.7, 11.9) + uTime * 0.07)) - 0.5;
    vec2 q = sp + uWarp * (w1 * 1.20 + w2 * 0.75) * 2.0;` :
`    vec2 q = sp + uWarp * w1 * 2.2;`}
${prof === 'nonoise' ? '    q = sp;' : ''}

    // THE SEARCH CARRIES FLOATS ONLY. Copying the two colours along with the
    // two distances costs two vec3 moves an iteration, and measured on the Pi
    // that was 34.8 ms of the loop's 63.4 - more than the distance arithmetic
    // it was there to accompany. The indices ride through instead, and the two
    // colours are fetched once, afterwards.
    float d1 = 1e9, d2 = 1e9;
    float i1 = 0.0, i2 = 0.0;
${prof === 'noloop' ? `    d1 = length(q) - 0.4; d2 = d1 + 0.2;
    for (int i = 0; i < 0; i++) {` : `    for (int i = 0; i < ${N}; i++) {`}
      // Additively weighted: the radius is SUBTRACTED, which is what makes a
      // big cell push its boundary out against a small one.
      float d = distance(q, uSite[i].xy) - uSite[i].z * uPress;
      if (d < d1) {
        d2 = d1; i2 = i1;
        d1 = d; i1 = float(i);
      } else if (d < d2) {
        d2 = d; i2 = float(i);
      }
    }
    // Fetched by index, once. A second pass over the array to find them cost
    // 37 ms on its own - worse than the copies it was meant to replace, because
    // every pixel takes a different branch and V3D serialises them.
    vec3 c1 = uColor[int(i1)];
    vec3 c2 = uColor[int(i2)];

    // -d1 runs out where the oil ends in water: a ROUNDED edge.
    // (d2-d1) runs out where two cells press: a STRAIGHT one.
    float edge = min(-d1, (d2 - d1) * 0.5);
    // THE MENISCUS IS SPECIFIED IN PIXELS, so the field has to be converted
    // into them - and fwidth does it exactly, at whatever resolution, with no
    // uniform to keep in step. Passing the buffer height in instead worked on
    // the app and silently missed in the bench, where nothing calls the resize
    // hook: the lines came out 1.6 times too thin and the whole triple line
    // read as a single soft edge.
    float px = edge / max(fwidth(edge), 1e-6);

    if (uSites > 0.5) {
      gl_FragColor = vec4(vec3(clamp(d2 - d1, 0.0, 1.0) * 3.0,
                               clamp(-d1, 0.0, 1.0) * 3.0, 0.2), 1.0);
      return;
    }

    // THE LAMP THROUGH CLEAR WATER, and the falloff is ROUND because the glass
    // is: sp is already aspect-corrected, so length(sp) is a true circle on
    // screen and the four corners - which sit furthest out - go into arc. An
    // elliptical falloff in uv, which is what was here, darkens the frame like
    // a photographic vignette and reads as a wallpaper rather than as light
    // coming through a disc.
    float rad = length(sp);
    float lamp = 1.0 - smoothstep(0.0, ${LAMP_FALL.toFixed(2)}, rad);
    vec3 col = mix(uWaterEdge, uWater, lamp);

    if (px > 0.0) {
      vec3 oil = c1;
${translucent}
      // A LENS, NOT A FLAT FILL. The patty is thicker in the middle than at its
      // rim, so it holds more dye there: t runs 1 at the site and 0 at the
      // contour, and the dye is squared towards the middle, which saturates as
      // well as darkens - the way more of the same filter would.
      float r1 = uSite[int(i1)].z * uPress;
      float t = clamp(-d1 / max(r1, 1e-3), 0.0, 1.0);
      vec3 thick = mix(oil, oil * oil, t * ${CENTRE_DENSITY.toFixed(2)});

      // And it is never perfectly homogeneous.
      thick *= 1.0 + (vnoise(sp * 6.3 + 31.7) - 0.5) * 2.0 * ${OIL_GRAIN.toFixed(3)};

      // The dense band, in the last few pixels before the rim: surface tension
      // banks the oil up there. Narrow, and on top of the broad gradient above.
      float dense = 1.0 - smoothstep(0.0, ${LINE_DENSE.toFixed(1)}, px);
      col *= mix(thick, thick * oil, dense * 0.55);
    }

    // THE DARK LINE, exactly on the contour, two pixels wide, and NEUTRAL. It
    // used to scale whatever colour was already there, which at a cell-cell
    // boundary meant darkening an already saturated overlap: the three elements
    // collapsed into one coloured seam. Pulling towards black instead keeps it
    // legible as a line whatever is on either side of it - and once the cells
    // are pressed together, nearly every contour IS a cell-cell boundary.
    float dark = 1.0 - smoothstep(0.0, ${(LINE_DARK * 0.5).toFixed(1)}, abs(px));
    col = mix(col, col * 0.10, dark * 0.90);
${brightLine}
${disc}

    // Dither. The lamp gradient is shallow and covers the whole frame, so it
    // bands on an 8-bit panel and the banding is the first thing the eye finds.
    col += (hash12(gl_FragCoord.xy + uTime) - 0.5) * (1.8 / 255.0);
    col = min(col, vec3(${PEAK.toFixed(2)}));
    col *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, clamp((vSafe - 0.32) / 0.68, 0.0, 1.0));
    gl_FragColor = vec4(col, 1.0);
  }
`;
}

// =============================================================================

export class LiquidLightVisualizer extends Visualizer {
  constructor(options) {
    super();
    const opts = options || {};
    let tier = opts.qualityTier != null ? opts.qualityTier : DEFAULT_TIER;
    if (!TIER[tier]) tier = 0;
    this._tier = tier;
    this._layout = TIER[tier];

    this._time = 0;
    this._paused = false;
    this._lastBeatPhase = 0;
    this._lastBarPhase = 0;
    this._lastDropAt = -1e9;
    this._press = 0;             // the envelope, 0 at rest
    this._pressTarget = 0;
    this._pressGain = 1;
    this._warp = WARP_BASE;
    this._lowBand = 0.5;
    this._midBand = 0.5;
    this._highBand = 0.5;
    this._tiltAt = 0;
    this._tiltUntil = -1e9;
    this._tiltDir = 1;
    this._activeCells = 0;
    this._lastPalette = null;
    this._vivid = [0xd8456b, 0x3fa8d8, 0xf0b93a, 0x7a4fd0, 0x36c08a];

    this._forcePress = urlParam('liqpress') === '1';
    this._forceTilt = urlParam('liqtilt') === '1';
    this._showSites = urlParam('liqsites') === '1';
    this._prof = urlParam('liqprof');
    const fc = parseInt(urlParam('liqcells'), 10);
    this._forceCells = Number.isFinite(fc) ? fc : null;
  }

  init(scene) {
    this._scene = scene;
    const L = this._layout;
    const N = L.cells;

    // Every slot exists in the shader; `_activeCells` decides how many are in
    // the water. A sleeping cell is parked far off with a zero radius rather
    // than branched around, because a branch inside the loop costs more than
    // the iteration it saves.
    // A LOWER TIER SHOWS FEWER CELLS, SO ITS CELLS ARE BIGGER. Without this the
    // plate simply empties as the tier drops - measured at 45% of the frame
    // oiled at twenty-two cells against 24% at ten - and the crushed look,
    // which is the whole point, goes with it. Scaling the radius by the square
    // root of the count keeps roughly the same quantity of oil on the glass at
    // every tier.
    this._rScale = Math.sqrt(CELLS_MAX / N);
    this._sites = [];
    for (let i = 0; i < N; i++) {
      this._sites.push({
        x: (hash1(i * 3.1) - 0.5) * SITE_SPREAD_X,
        y: (hash1(i * 7.7) - 0.5) * SITE_SPREAD_Y,
        r: (RADIUS[0] + hash1(i * 11.3) * (RADIUS[1] - RADIUS[0])) * this._rScale,
        seed: hash1(i * 5.3) * 10,
        heat: hash1(i * 13.1),
        vx: 0,
        vy: 0,
        col: new THREE.Color(1, 1, 1)
      });
    }

    const siteArr = [];
    const colArr = [];
    for (let i = 0; i < N; i++) {
      siteArr.push(new THREE.Vector4(0, 0, 0, 0));
      colArr.push(new THREE.Vector3(1, 1, 1));
    }
    this._uniforms = Object.assign({
      uSite: { value: siteArr },
      uColor: { value: colArr },
      uPress: { value: 1 },
      uWarp: { value: WARP_BASE },
      uHigh: { value: 0.5 },
      uBandPx: { value: 14 },
      uTime: { value: 0 },
      uWater: { value: new THREE.Vector3(0.560, 0.540, 0.498) },
      uWaterEdge: { value: new THREE.Vector3(0.250, 0.240, 0.220) },
      uSites: { value: this._showSites ? 1 : 0 }
    }, safeZoneUniforms);

    this._mat = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: QUAD_VERTEX,
      fragmentShader: fragment(L, this._prof),
      depthTest: false,
      depthWrite: false
    });
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._mat);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -500;
    scene.add(this._quad);

    this._activeCells = Math.min(N, CELLS_MIN);
    this._applyPalette(null);
    this._scheduleTilt();
    this._writeSites();
  }

  _scheduleTilt() {
    this._tiltAt = this._time + TILT_EVERY[0]
      + Math.random() * (TILT_EVERY[1] - TILT_EVERY[0]);
  }

  // --- the frame -------------------------------------------------------------

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.05);
    this._paused = profile.playbackState === 'PAUSED';
    if (this._paused) return;    // pause: the plates are simply not touched
    this._time += dt;

    const director = profile.director || {};
    const bands = director.bands || { low: 0.5, mid: 0.5, high: 0.5 };
    this._lowBand = clamp01(bands.low);
    this._midBand = clamp01(bands.mid);
    this._highBand = clamp01(bands.high);

    const palette = profile.artworkPalette;
    if (palette && palette !== this._lastPalette) {
      this._lastPalette = palette;
      this._applyPalette(palette);
    }

    // progress opens the dish from fourteen cells to twenty-two at mid-track.
    const arc = Math.sin(clamp01(profile.progress || 0) * Math.PI);
    const want = this._forceCells != null ? this._forceCells
      : Math.round(CELLS_MIN + (CELLS_MAX - CELLS_MIN) * arc);
    this._activeCells = Math.max(1, Math.min(this._layout.cells, want));

    const beatPhase = profile.beatPhase || 0;
    const barPhase = profile.barPhase || 0;
    const hasGrid = director.hasReliableGrid !== false && profile.beatPhase != null;

    let bar = false;
    if (hasGrid) {
      if (barPhase < this._lastBarPhase - 0.25) { this._squeeze(PRESS_BAR); bar = true; }
      if (beatPhase < this._lastBeatPhase - 0.25 && !bar) this._squeeze(1);
      this._lastBarPhase = barPhase;
      this._lastBeatPhase = beatPhase;
    } else {
      // No grid: the operator presses on the rises in the low end instead.
      const rise = this._lowBand - (this._lastLow || 0);
      if (rise > 0.11 && this._time - (this._lastFreePress || 0) > 0.30) {
        this._lastFreePress = this._time;
        this._squeeze(rise > 0.25 ? PRESS_BAR : 1);
      }
      this._lastLow = this._lowBand;
    }

    const ev = director.nextEvent;
    if (ev && ev.type === 'drop' && ev.msUntil != null && ev.msUntil <= 0
        && this._time - this._lastDropAt > 2.5) {
      this._lastDropAt = this._time;
      this._squeeze(PRESS_DROP);
    }
    if (this._forcePress) { this._forcePress = false; this._squeeze(PRESS_BAR); }

    // THE PRESS ENVELOPE. One global number, so every radius grows at the same
    // instant - that simultaneity is the gesture. Attack and release are
    // different lengths, which is what makes it read as a squeeze and not as a
    // throb.
    // RAMPS, not a lag. The brief says the squeeze arrives in 60 ms and comes
    // back in 400; an exponential approach only reaches 63% in its time
    // constant, and the measured peak landed at 167 ms - nearly three times
    // late, which on a beat is the difference between a press and a swell.
    if (this._press < this._pressTarget) {
      this._press = Math.min(this._pressTarget, this._press + dt / PRESS_ATTACK);
      if (this._press >= this._pressTarget - 1e-4) this._pressTarget = 0;
    } else {
      this._press = Math.max(0, this._press - dt / PRESS_RELEASE);
    }

    const amp = PRESS_BEAT * (0.55 + 0.9 * this._lowBand);
    this._uniforms.uPress.value = 1 + this._press * amp;
    this._warp += ((bar ? WARP_BAR : WARP_BASE) - this._warp) * Math.min(1, 4 * dt);
    this._uniforms.uWarp.value = this._warp * (1 + 0.5 * this._press);
    this._uniforms.uHigh.value = this._highBand;
    this._uniforms.uTime.value = this._time % 512;

    if (this._forceTilt) { this._forceTilt = false; this._startTilt(); }
    if (this._time > this._tiltAt) this._startTilt();

    this._drift(dt);
    this._writeSites();
  }

  _squeeze(strength) {
    this._pressTarget = Math.max(this._pressTarget || 0, strength);
  }

  _startTilt() {
    this._tiltUntil = this._time + TILT_DUR;
    this._tiltDir = Math.random() < 0.5 ? -1 : 1;
    this._scheduleTilt();
  }

  // Convection, plus the operator's tilt. The lamp is underneath, so the middle
  // rises and the sides fall; a cell that has spent a long time in the hot
  // middle runs a little fatter.
  _drift(dt) {
    const speed = CONVECT * (0.5 + 1.3 * this._midBand);
    const tilting = this._time < this._tiltUntil;
    const lateral = tilting ? this._tiltDir * TILT_SPEED : 0;
    for (let i = 0; i < this._sites.length; i++) {
      const s = this._sites[i];
      // Up the middle, down the sides.
      const up = Math.cos(s.x * 1.25) * speed;
      const side = -Math.sin(s.x * 1.25) * speed * 0.55;
      s.vx = side + lateral + (hash1(i * 2.1 + Math.floor(this._time * 0.7)) - 0.5) * SITE_DRIFT;
      s.vy = up + (hash1(i * 4.3 + Math.floor(this._time * 0.6)) - 0.5) * SITE_DRIFT;
      s.x += s.vx * dt * 60 * 0.016;
      s.y += s.vy * dt * 60 * 0.016;
      // Time spent near the middle, which is where the lamp is hottest.
      s.heat = clamp01(s.heat + (Math.abs(s.x) < 0.5 ? 0.06 : -0.05) * dt);

      // A site that reaches an edge comes back in at the bottom with a new
      // colour: the dish never empties and never crowds.
      if (s.y > SITE_SPREAD_Y * 0.75 || s.x < -SITE_SPREAD_X * 0.80
          || s.x > SITE_SPREAD_X * 0.80 || s.y < -SITE_SPREAD_Y * 0.80) {
        s.y = -SITE_SPREAD_Y * 0.62;
        s.x = (hash1(this._time * 7.3 + i) - 0.5) * SITE_SPREAD_X;
        s.heat = 0;
        this._recolour(i);
      }
    }
  }

  _writeSites() {
    const sites = this._uniforms.uSite.value;
    const cols = this._uniforms.uColor.value;
    for (let i = 0; i < this._sites.length; i++) {
      const s = this._sites[i];
      const live = i < this._activeCells;
      sites[i].set(s.x, s.y, live ? s.r * (1 + HEAT_GAIN * s.heat) : 0, s.seed);
      if (!live) { sites[i].x = 99; sites[i].y = 99; }
      cols[i].set(s.col.r, s.col.g, s.col.b);
    }
  }

  _recolour(i) {
    const s = this._sites[i];
    _col.set(this._vivid[Math.floor(Math.random() * this._vivid.length)]);
    _col.getHSL(_hsl);
    // Saturation floor of 0.8, and a light value that stays a FILTER: a dye too
    // pale stops darkening what is behind it and the overlaps vanish.
    _col.setHSL(_hsl.h, Math.max(0.80, _hsl.s), Math.min(0.62, Math.max(0.42, _hsl.l)));
    s.col.copy(_col);
    s.r = (RADIUS[0] + Math.random() * (RADIUS[1] - RADIUS[0])) * this._rScale;
  }

  _applyPalette(palette) {
    if (palette && palette.vividColors && palette.vividColors.length) {
      this._vivid = palette.vividColors;
    }
    for (let i = 0; i < this._sites.length; i++) this._recolour(i);
    if (palette && palette.dominantColors && palette.dominantColors.length) {
      _col.set(palette.dominantColors[0]);
      // The water is lamp light, barely tinted by the cover - it must stay a
      // warm white, not become a colour.
      // SWEPT AGAINST THE RENDERED FRAME, not converted on paper. Two things
      // fight over this number. Too high and the pipeline blooms - at 0.95 the
      // whole picture was over the 0.82 threshold, every cell washed to pastel
      // and the middle blew out white. Too high ALSO leaves no headroom above
      // the water for the bright meniscus, which is what made that line
      // measure as absent however hard it was pushed. Measured through the
      // composer: 0.44 shows the water at 0.746 - a warm off-white - and still
      // leaves the meniscus 0.125 of margin above it.
      // Tinted by the cover, but only just - a tenth at most, because this is
      // lamp light through clear water and not a coloured backdrop.
      const t = 0.10;
      this._uniforms.uWater.value.set(
        0.560 * (1 - t) + _col.r * t, 0.540 * (1 - t) + _col.g * t,
        0.498 * (1 - t) + _col.b * t);
      this._uniforms.uWaterEdge.value.set(
        0.250 * (1 - t) + _col.r * t * 0.45, 0.240 * (1 - t) + _col.g * t * 0.45,
        0.220 * (1 - t) + _col.b * t * 0.45);
    }
    this._writeSites();
  }

  dispose() {
    if (this._quad) {
      if (this._scene) this._scene.remove(this._quad);
      this._quad.geometry.dispose();
      this._quad.material.dispose();
      this._quad = null;
    }
  }
}
